/*! Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
SPDX-License-Identifier: MIT-0 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ProgressCircle } from '@adobe/react-spectrum';
import { device, chatWidgetDefaults, chatParties, loggerNames } from '../utils/constants';
import { ChatContainer, ChatWrapper } from './AmazonChatStyled';
import PostMessageUtil from '../utils/PostMessageUtil';
import { genLogger } from "../lib/logger";
import Config from '../config/env_variables';

import BackendService from '../services/BackendService';
import '../assets/styles/widget.css';

const name = loggerNames.containers.CHAT_WIDGET;
var currentChatContactId = null;

const { log, logAlways, error, trace, info } = genLogger(name);
const chatWidgetConfig = {
    region: Config.REACT_APP_AMAZON_CHAT_REGION,
    apiGateway: Config.REACT_APP_AMAZON_CHAT_API_GATEWAY,
    enableAttachments: Config.REACT_APP_AMAZON_CHAT_ENABLE_ATTACHMENTS === "true" || chatWidgetDefaults.ENABLE_ATTACHMENTS,
};

export default function AmazonChatWidget(props) {
    log(">>> Init Amazon connect chat widget");
    const primaryColor = props.primaryColor ?? chatWidgetDefaults.PRIMARY_COLOR;
    const enableAttachments = props.enableAttachments || chatWidgetConfig.enableAttachments;
    const description = props.description ?? chatWidgetDefaults.DESCRIPTION;
    const uiVersion = props.uiVersion ?? (
        document.body?.classList.contains('ui-v2') ? 'v2' : undefined
    );

    const OUTGOING_MESSAGES_TYPE = PostMessageUtil.outgoingMessageTypes;

    const [isLoading, setIsLoading] = useState(true);
    const { region, apiGateway } = chatWidgetConfig;
    const { contactFlowId } = props;
    // eslint-disable-next-line

    const postMessageHandler = (e) => {
        const { postMessageHandlerFunc } = props;
        postMessageHandlerFunc(e);
      };

    const successHandler = (chatSession) => {
        info("successHandler");
        setIsLoading(false);
        log("Chat session started", { contactId: chatSession?.contactId });
        const sessionChatData = {
            contactId: chatSession?.contactId,
            email: props?.email,
            orgId: props?.orgId,
            instanceId: props?.instanceId,
            chatSurface: props?.chatSurface?.replace('https://www.', '').replace('https://', '').replace('http://', '')
        };
        currentChatContactId = chatSession?.contactId;
        localStorage.setItem("sessionChatData", JSON.stringify(sessionChatData));

        // Call /generateContactSession to get a contact session id
        BackendService.generateContactSession({contactId: chatSession?.contactId})
            .then(response => {
                log("Contact session generated successfully");
                const oldSessionChatData = JSON.parse(localStorage.getItem("sessionChatData"));
                const newSessionChatData = {
                    ...oldSessionChatData,
                    sessionId: response?.sessionId
                };
                localStorage.setItem("sessionChatData", JSON.stringify(newSessionChatData));
            })
            .catch(err => {
                error("Failed to generate contact session:", err?.message || err);
            });

        // Handle incoming messages from Amazon Connect
        chatSession.incomingItemDecorator = function (item) {
            if ([chatParties.SYSTEM_MESSAGE].indexOf(item.displayName) !== -1) {
                item.displayName = "";
            }

            if ([chatParties.BOT].indexOf(item.displayName) !== -1) {
                item.displayName = "";
            }
            return item;
        }
        chatSession.onIncoming(function (data) {
            trace("incoming message:|| " + JSON.stringify(data));
        });

        chatSession.onOutgoing(function (data) {
            trace("outgoing message:|| " + JSON.stringify(data));
        });
        
        chatSession.onChatDisconnected(function (data) {
            info("Chat has been disconnected");
            trace(data);
            // setTimeout(() => {
            //     postMessageHandler({ data, msgType: OUTGOING_MESSAGES_TYPE.CONV_CLOSED });
            // }, 100000);
        });
    };

    const failureHandler = (e) => {
        error("failed", e);
        currentChatContactId = null;
        props.onError();
    };

    const getNameAndUserName = (initialPropertiesName='', initialPropertiesUsername='') => {
        log('Inside getNameAndUserName');
        let name = chatWidgetDefaults.NAME;
        let username = chatWidgetDefaults.USER_NAME;

        // If props.is available
        name = initialPropertiesName ? initialPropertiesName : chatWidgetDefaults.NAME;
        username = initialPropertiesUsername ? initialPropertiesUsername : chatWidgetDefaults.USER_NAME;

        return {name, username}
    }

    const getBrowserInfo = () => {
        try {
            const userAgent = navigator?.userAgent;
            let browserName;
            let browserVersion;
            let operatingSystem;
    
            // Detect browser name and version
            if (userAgent.indexOf("Edg") > -1) {
                browserName = "Microsoft Edge";
                browserVersion = userAgent.substring(userAgent.indexOf("Edg") + 4).split(" ")[0];
            } else if (userAgent.indexOf("Chrome") > -1) {
                browserName = "Chrome";
                browserVersion = userAgent.substring(userAgent.indexOf("Chrome") + 7).split(" ")[0];
            } else if (userAgent.indexOf("Firefox") > -1) {
                browserName = "Firefox";
                browserVersion = userAgent.substring(userAgent.indexOf("Firefox") + 8).split(" ")[0];
            } else if (userAgent.indexOf("Safari") > -1) {
                browserName = "Safari";
                browserVersion = userAgent.substring(userAgent.indexOf("Version") + 8).split(" ")[0];
            } else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) {
                browserName = "Internet Explorer";
                browserVersion = userAgent.substring(userAgent.indexOf("MSIE") + 5).split(" ")[0];
            } else {
                browserName = "Unknown";
                browserVersion = "N/A";
            }
    
            // Detect operating system
            if (userAgent.indexOf("Windows") > -1) {
                operatingSystem = "Windows";
            } else if (userAgent.indexOf("Mac") > -1) {
                operatingSystem = "Mac OS";
            } else if (userAgent.indexOf("Linux") > -1) {
                operatingSystem = "Linux";
            } else if (userAgent.indexOf("Android") > -1) {
                operatingSystem = "Android";
            } else if (userAgent.indexOf("iOS") > -1) {
                operatingSystem = "iOS";
            } else if (userAgent.indexOf("BlackBerry") > -1) {
                operatingSystem = "BlackBerry OS";
            } else if (userAgent.indexOf("Windows Phone") > -1 || userAgent.indexOf("IEMobile") > -1) {
                operatingSystem = "Windows Phone";
            } else {
                operatingSystem = "Unknown";
            }
    
            return {
                browserName: browserName,
                browserVersion: browserVersion,
                operatingSystem: operatingSystem
            };
        } catch (err) {
            error("Error occurred while getting browser information:", err?.message || err);
            return {
                browserName: null,
                browserVersion: null,
                operatingSystem: null
            };
        }
    }
    
    const getContactAttrsForContactFlow = (name = '', username = '') => {
        log('>>> Inside getContactAttrsForContactFlow');
        let attrs = {};
        const {browserName, browserVersion, operatingSystem} =  getBrowserInfo();
        attrs.customerName = name;
        attrs.username = username;
        attrs.rengaId = props.userId;
        attrs.fullName = props.fullName;
        attrs.firstName = props.firstName;
        attrs.lastName = props.lastName;
        //attrs.phoneNumber = props.phoneNumber;
        attrs.email = props.email;
        attrs.country = props.country;
        attrs.lang = props.lang;
        attrs.uiVersion = uiVersion ?? 'v1';
        attrs.entitlements = props.entitlements;
        attrs.marketoChamp = props.marketoChamp;
        attrs.issue = props.issue;
        // browser related attributes
        attrs.browser = browserName;
        attrs.browserVersion = browserVersion;
        attrs.os = operatingSystem;
        //attrs.productOption = props.productOption;
        attrs.product = props.product;
        attrs.orgId = props.orgId;
        attrs.instanceId = props.instanceId;
        //remove prefix from url 
        const chatSurface = props.chatSurface?.replace('https://www.', '').replace('https://', '').replace('http://', '');
        attrs.chatSurface = chatSurface;
        // If contactAttr was sent from the initial config add that as well:
        // if (initialPropertiesContAttrs) {
        //     log(`Appending ${initialPropertiesContAttrs} to contact attributes`)
        //     attrs = { ...attrs, ...initialPropertiesContAttrs }
        // }
        return attrs;
    }
    const initAmazonConnectChat = () => {
        try {
            // Set default name and username:
            const { name, username } = getNameAndUserName(props.fullName, props.email);
            const { persistentChat } = props;
            currentChatContactId = null;
            log('Initiating chat connection');

            //Set contact attributes for contact flow:
            let contactAttrs = getContactAttrsForContactFlow(name, username);
            logAlways('Chat UI version:', contactAttrs.uiVersion);
            log('contactAttrs for contact flow (redacted): ', {
                product: contactAttrs.product,
                orgId: contactAttrs.orgId,
                instanceId: contactAttrs.instanceId,
                lang: contactAttrs.lang,
                uiVersion: contactAttrs.uiVersion,
                browser: contactAttrs.browser,
                browserVersion: contactAttrs.browserVersion,
                os: contactAttrs.os,
                chatSurface: contactAttrs.chatSurface,
            });
            let params = {
                name,
                username,
                region,
                apiGatewayEndpoint: apiGateway,
                contactAttributes: JSON.stringify(contactAttrs),
                contactFlowId,
                primaryColor: '#F5F5F5',
                instanceId: props.amazonConnectInstanceId,
                featurePermissions: {
                    "ATTACHMENTS": !!enableAttachments,  // this is the override flag from user for attachments
                    MESSAGING_MARKDOWN: true
                },
                supportedMessagingContentTypes: "text/plain,text/markdown",
            }
            try {
                const matchParamsString = Config?.REACT_APP_AMAZON_CHAT_ASYNC_MATCH_PARAMS;
                const matchParams = matchParamsString?.split(',');
                // Retrieve sessionChatData from localStorage
                const sessionChatData = localStorage.getItem("sessionChatData");
                if (sessionChatData) {
                    const savedParams = JSON.parse(sessionChatData);
                    // Check if sessionChatData exists and is an object
                    if (matchParams && savedParams && typeof savedParams === 'object') {
                        // Iterate over global keys
                        let shouldStartPersistantChat = true;
                        for (const key of matchParams) {
                            // Check if the key exists in sessionChatData and if it matches the corresponding prop
                            const isMatch = !!savedParams[key] && !!props[key]?.includes(savedParams[key]);
                            log("Checking if saved param matches props", key, isMatch);
                            if (!isMatch) {
                                log("Saved param does not match props, will start new chat", key);
                                shouldStartPersistantChat = false;
                            }
                        }
                        if(shouldStartPersistantChat) {
                            log("Starting persistent chat with saved params", Object.keys(savedParams));
                            if (persistentChat && savedParams["contactId"]) {
                                params = {...params, 
                                    persistentChat: {
                                        rehydrationType: "ENTIRE_PAST_SESSION", 
                                        sourceContactId: savedParams["contactId"]
                                    },
                                }
                            }
                        }
                    }
                }
                
            } catch(err) {
                error("Error occurred while adding persistentChat params, will start new chat", err?.message || err);
            }
            
            window.connect.ChatInterface.initiateChat(params, successHandler, failureHandler);
        }
        catch (e) {
            error('Error: window.connect.ChatInterface.initiateChat failed due to error =');
            error(e);
            props.onError();
        }
    }
    const initAmazonConnectChatWidget = () => {
        try {
            window.connect.ChatInterface.init({
                containerId: 'chat-widget', // This is the id of the container where you want the widget to reside
                headerConfig: {      // Use the optional headerConfig and footerConfig to customize your widget
                    isHTML: true,
                    render: () => {
                    return (`<div class="header-wrapper">
                                <h2 class="welcome-text">${description}</h2>
                            </div>`)
                    }
                },
            });
            // Amazon's own widget shell (with its own connecting indicator) is mounted by init() -
            // hand off from our loader here instead of waiting for the connection itself to complete.
            setIsLoading(false);

            initAmazonConnectChat();
        } catch(initError) {
            error("initAmazonConnectChatWidget failed due to error", initError?.message || initError);
            props.onError();
        }
    }
    // eslint-disable-next-line
    useEffect(() => {
        setIsLoading(true);
        log("useEffect");
        const script = document.createElement('script');
        script.src = '/amazon-connect-chat-interface.js';
        script.async = true;
        script.onload = () => initAmazonConnectChatWidget();
        document.body.appendChild(script);

        window.addEventListener('beforeunload', (e) => {
            e.preventDefault();
        });

        window.addEventListener('closeChat', (e)=>{
            e.preventDefault();
        });

        window.addEventListener('downloadTranscript',(e)=>{
         const date = new Date();
         const timezone = date.toTimeString().substring(9);
         const cid = currentChatContactId || JSON.parse(localStorage.getItem("sessionChatData")).contactId;
         const sessionId = JSON.parse(localStorage.getItem("sessionChatData")).sessionId;
         BackendService.fetchFileHandlerAWS(props.accessToken, cid, sessionId, null, timezone);
        })

        window.addEventListener('adbmsg_FILE_DOWNLOAD', (event)=> {
            try {
                if(event.detail && event.detail.blob && event.detail.fileName) {
                    BackendService.sendFileToAppAsBase64(event.detail?.blob, event.detail.fileName);
                } else {
                    log("No file blob or filename is found in detail")
                }
            } catch(err) {
                error("Error handling file download event:", err?.message || err);
            }
        });
        
        return () => {
            document.body.removeChild(script);
        }; 
    }, [])

    return (
        <ChatContainer id="chat-container" device={device} uiVersion={uiVersion}>
            <ChatWrapper id="chat-wrapper" primaryColor={primaryColor} uiVersion={uiVersion} device={device} style={{ position: 'relative' }}>
                {isLoading && (
                    <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                    }}
                    >
                        <ProgressCircle aria-label="Loading…" isIndeterminate />
                    </div>
                )}
                <div id="chat-widget" data-cs-mask="true"></div>
            </ChatWrapper>
        </ChatContainer>
    );
};

AmazonChatWidget.propTypes = {
    postMessageHandlerFunc: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    product: PropTypes.string.isRequired,
    orgId: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    // imsAccessToken: PropTypes.string.isRequired,
    fullName: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    //phoneNumber: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    lang: PropTypes.string.isRequired,
    entitlements: PropTypes.string.isRequired,
    marketoChamp: PropTypes.string.isRequired,
    issue: PropTypes.string.isRequired,
    accessToken: PropTypes.string.isRequired,
    //Amazon connect specific
    primaryColor: PropTypes.string,
    description: PropTypes.string,
    enableAttachments: PropTypes.string,
    persistentChat: PropTypes.string,
    contactFlowId: PropTypes.string,
    amazonConnectInstanceId: PropTypes.string.isRequired,
    instanceId: PropTypes.string,
    chatSurface: PropTypes.string,
    uiVersion: PropTypes.string,
};